import React, { useRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import QRCode from 'react-native-qrcode-svg';
import ReactDOM from 'react-dom';

const isMobile = (typeof window !== 'undefined' && window.innerWidth < 768) || (Platform.OS !== 'web' && Platform.OS !== undefined);
const isMobileOrTablet = (typeof window !== 'undefined' && window.innerWidth < 1024) || (Platform.OS !== 'web' && Platform.OS !== undefined);

const QRCodeShare = ({ url, size = 200, style = {} }) => {
  const qrRef = useRef();
  const canvasRef = useRef();
  const [showCopied, setShowCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // Listen for download events from parent components
  useEffect(() => {
    const handleDownloadEvent = () => {
      handleDownload();
    };
    
    if (Platform.OS === 'web') {
      document.addEventListener('downloadQR', handleDownloadEvent);
      return () => {
        document.removeEventListener('downloadQR', handleDownloadEvent);
      };
    }
  }, []);

  // Draw QR code SVG onto canvas for copying
  const drawToCanvas = (onDraw, transparent = false) => {
    let svg = qrRef.current && qrRef.current._svg;
    if (!svg && typeof document !== 'undefined') {
      svg = document.querySelector('svg');
    }
    const canvas = canvasRef.current;
    if (!svg || !canvas) {
      setCopyError(true);
      return;
    }
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    if (!svgString || svgString.length < 100) {
      setCopyError(true);
      return;
    }
    const img = new window.Image();
    const svg64 = btoa(unescape(encodeURIComponent(svgString)));
    const image64 = 'data:image/svg+xml;base64,' + svg64;
    img.onload = function () {
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!transparent) {
        ctx.clearRect(0, 0, size, size);
      } else {
        // For transparent background, clear the canvas but don't fill with white
        ctx.clearRect(0, 0, size, size);
      }
      ctx.drawImage(img, 0, 0, size, size);
      if (onDraw) onDraw();
    };
    img.onerror = function () {
      setCopyError(true);
    };
    img.src = image64;
  };

  const handleCopy = async () => {
    if (Platform.OS !== 'web') return;
    setCopyError(false);
    drawToCanvas(() => {
      setTimeout(async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.toBlob(async (blob) => {
          if (!blob) {
            setCopyError(true);
            return;
          }
          // Check if the blob is not just a white square (very small size)
          if (blob.size < 200) {
            setCopyError(true);
            return;
          }
          try {
            await navigator.clipboard.write([
              new window.ClipboardItem({ [blob.type]: blob })
            ]);
            setShowCopied(true);
            setTimeout(() => setShowCopied(false), 1500);
          } catch (err) {
            setCopyError(true);
          }
        }, 'image/png');
      }, 100); // Wait for canvas to draw
    });
  };

  const printQRCode = () => {
    if (Platform.OS !== 'web') return;
    drawToCanvas(() => {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
          console.error('Canvas not available for printing');
          return;
        }
        const dataURL = canvas.toDataURL('image/png');
        const printWindow = window.open('', '_blank');
        const printContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print QR Code</title>
              <style>
                body {
                  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                  margin: 0;
                  padding: 20px;
                  background-color: #fefde2;
                }
                .qr-container {
                  text-align: center;
                  background-color: #fefde2;
                  padding: 40px;
                  border-radius: 10px;
                  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                }
                .qr-code {
                  margin: 20px 0;
                  display: flex;
                  justify-content: center;
                }
                .qr-code img {
                  width: 200px;
                  height: 200px;
                }
                .description {
                  color: #4c956c;
                  font-size: 18px;
                  font-weight: bold;
                  margin: 20px 0;
                  text-align: center;
                  max-width: 400px;
                  line-height: 1.4;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  min-height: 48px;
                }
                @media print {
                  body { 
                    margin: 0; 
                    padding: 0;
                  }
                  .qr-container { 
                    box-shadow: none; 
                    padding: 20px;
                  }
                }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <div class="qr-code">
                  <img src="${dataURL}" alt="QR Code" />
                </div>
                <div class="description">
                  Going old-school with printed announcements? Add a QR code that will link straight to your MyTassel profile.
                </div>
              </div>
              <script>
                window.onload = function() {
                  setTimeout(function() {
                    window.print();
                    window.close();
                  }, 500);
                };
              </script>
            </body>
          </html>
        `;
        printWindow.document.write(printContent);
        printWindow.document.close();
      }, 100);
    });
  };

  // Download QR code as PNG with transparent background
  const handleDownload = () => {
    drawToCanvas(() => {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Create PNG with transparent background
        const dataURL = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = 'MyTassel-QR-Code.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 100);
    }, true); // Pass true for transparent background
  };

  // Print QR code only (no text)
  const printQRCodeOnly = () => {
    if (Platform.OS !== 'web') return;
    drawToCanvas(() => {
      setTimeout(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dataURL = canvas.toDataURL('image/png');
        const printWindow = window.open('', '_blank');
        const printContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Print QR Code</title>
              <style>
                body {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                  margin: 0;
                  background: #fff;
                }
                .qr-code {
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }
                .qr-code img {
                  width: 200px;
                  height: 200px;
                }
              </style>
            </head>
            <body>
              <div class="qr-code">
                <img src="${dataURL}" alt="QR Code" />
              </div>
              <script>
                window.onload = function() {
                  setTimeout(function() {
                    window.print();
                    window.close();
                  }, 500);
                };
              </script>
            </body>
          </html>
        `;
        printWindow.document.write(printContent);
        printWindow.document.close();
        setShowPrintModal(false);
      }, 100);
    });
  };

  // Toast portal overlay style
  const toastOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  };
  const toastBoxStyle = {
    backgroundColor: '#202020',
    color: '#fff',
    borderRadius: 10,
    padding: '12px 28px',
    fontSize: 16,
    fontWeight: 'normal',
    boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
    pointerEvents: 'auto',
    textAlign: 'center',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  };

  return (
    <View style={[styles.container, style]}>
      <QRCode value={url} size={size} getRef={qrRef} />
      {Platform.OS === 'web' && (
        <>
          <div style={styles.buttonContainer}>
            <button style={styles.copyButton} onClick={handleCopy}>Copy QR Code</button>
            <button style={styles.confirmPrintButton} onClick={handleDownload}>
              Download PNG
            </button>
            <button style={styles.confirmPrintButton} onClick={() => setShowPrintModal(true)}>
              Print QR Code
            </button>
          </div>
          {/* Hidden canvas for copying */}
          <canvas ref={canvasRef} width={size} height={size} style={{ display: 'none' }} />
        </>
      )}
      {showCopied && Platform.OS === 'web' && ReactDOM.createPortal(
        <div style={toastOverlayStyle}>
          <div style={toastBoxStyle}>QR Code copied to clipboard</div>
        </div>,
        document.body
      )}
      {copyError && Platform.OS === 'web' && ReactDOM.createPortal(
        <div style={toastOverlayStyle}>
          <div style={toastBoxStyle}>Failed to copy QR code. Try again.</div>
        </div>,
        document.body
      )}
      {showPrintModal && Platform.OS === 'web' && ReactDOM.createPortal(
        <div style={styles.printModalOverlay}>
          <div style={styles.printModalBox}>
            <QRCode value={url} size={isMobile ? 200 : 250} />
            <div style={styles.printDescription}>
              Going old-school with printed announcements? Add a QR code that will link straight to your MyTassel profile.
            </div>
            <div style={styles.printButtonContainer}>
              <button style={styles.closePrintButton} onClick={() => setShowPrintModal(false)}>
                Close
              </button>
              <button style={styles.confirmPrintButton} onClick={printQRCodeOnly}>
                Print QR Code
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </View>
  );
};

const styles = {
  container: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#fefde2',
    borderRadius: 10,
    position: 'relative',
  },
  label: {
    marginTop: 10,
    color: '#4c956c',
    fontWeight: 'bold',
    fontSize: 16,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  copyButton: {
    color: '#fff',
    border: 'none',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: isMobile ? 14 : 16,
    outline: 'none',
    display: 'block',
    backgroundColor: "#4c956c",
    padding: isMobile ? 7.5 : 10,
    borderRadius: 5,
    alignItems: "center",
  },
  printDescription: {
    color: '#4c956c',
    fontSize: isMobile ? 14 : 16,
    fontWeight: 'bold',
    marginTop: 25,
    marginBottom: 10,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 1.4,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  printButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
    marginTop: 20,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  confirmPrintButton: {
    color: '#fff',
    border: 'none',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: isMobile ? 14 : 16,
    outline: 'none',
    display: 'block',
    backgroundColor: "#4c956c",
    padding: isMobile ? 7.5 : 10,
    borderRadius: 5,
    alignItems: "center",
    minWidth: 120,
  },
  closePrintButton: {
    color: '#4c956c',
    border: '2px solid #4c956c',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: isMobile ? 14 : 16,
    outline: 'none',
    backgroundColor: 'transparent',
    padding: isMobile ? 10 : 12,
    borderRadius: 5,
    minWidth: 80,
  },
  toast: {
    position: 'fixed',
    top: '40%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#202020',
    color: '#fff',
    padding: '12px 28px',
    borderRadius: 10,
    zIndex: 999999,
    minWidth: 0,
    maxWidth: '80vw',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    fontWeight: 'normal',
    fontSize: 16,
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  toastError: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#202020',
    color: '#fff',
    padding: '12px 28px',
    borderRadius: 10,
    zIndex: 999999,
    minWidth: 0,
    maxWidth: '80vw',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    fontWeight: 'normal',
    fontSize: 16,
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  toastText: {
    color: '#fff',
    fontWeight: 'normal',
    fontSize: 16,
    textAlign: 'center',
    letterSpacing: 0.2,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
  },
  printModalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    pointerEvents: 'auto',
  },
  printModalBox: {
    backgroundColor: '#fefde2',
    borderRadius: 20,
    padding: isMobile ? 20 : 30,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: isMobile ? 280 : 350,
    maxWidth: isMobile ? '90%' : 400,
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
};

export default QRCodeShare;